/**
 * M-A14 — Skill playground drawer (basic).
 *
 * Sprint 133 Stream C. Read-only preview of how a skill will be presented
 * to an agent: rendered instructions, activation config, and asset list.
 *
 * Live ephemeral-conversation playground (run the skill against a real
 * agent and inspect the resolved <skills> block + tool calls) is deferred.
 * Tracked as TODO below.
 */

import { Sparkles } from 'lucide-react';
import { unwrapImportedField } from '@/lib/importedField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge, Card, StatusBadge } from '@/components/ui';
import { MarkdownRenderer } from '@vanguard-atlas/shared/src/components/ui';
import { useI18n } from '@/i18n';
import type { Skill } from '@/features/skills';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skill: Skill;
}

export function SkillPlaygroundDrawer({ open, onOpenChange, skill }: Props) {
  const { t } = useI18n();
  const triggers = skill.activation?.triggers ?? [];
  const assets = skill.assets ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('admin.skills.playground.title', { name: skill.display_name })}
          </DialogTitle>
          <DialogDescription>
            {t('admin.skills.playground.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Activation */}
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2">{t('admin.skills.playground.activation')}</h4>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('admin.skills.playground.mode')}</span>
              <StatusBadge status="info">
                {skill.activation?.mode ?? 'auto'}
              </StatusBadge>
              {triggers.length > 0 && (
                <>
                  <span className="text-muted-foreground ml-2">{t('admin.skills.playground.triggers')}</span>
                  {triggers.map((t) => (
                    <Badge key={t} variant="secondary" className="font-mono text-xs">
                      {t}
                    </Badge>
                  ))}
                </>
              )}
              {triggers.length === 0 && skill.activation?.mode === 'auto' && (
                <span className="text-xs text-muted-foreground italic">
                  {t('admin.skills.playground.noTriggers')}
                </span>
              )}
            </div>
          </Card>

          {/* Instructions preview */}
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2">{t('admin.skills.playground.instructions')}</h4>
            {skill.instructions ? (
              skill.instructions_format === 'markdown' ? (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <MarkdownRenderer content={unwrapImportedField(skill.instructions)} />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-sm font-mono bg-muted p-3 rounded">
                  {unwrapImportedField(skill.instructions)}
                </pre>
              )
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t('admin.skills.playground.noInstructions')}
              </p>
            )}
          </Card>

          {/* Assets */}
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2">
              {t('admin.skills.playground.assets', { count: assets.length })}
            </h4>
            {assets.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                {t('admin.skills.playground.noAssets')}
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {assets.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center justify-between gap-2 border-b last:border-0 py-1"
                  >
                    <span className="font-mono text-xs">{a.asset_id}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {a.type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        inject: {a.inject}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/*
            TODO — live agent playground:
            • Pick an agent from the tenant
            • Create an ephemeral conversation (POST /conversations)
            • Force-attach this skill to the conversation
            • Render an embedded chat (re-use AgentTestChat) with the
              resolved <skills> block and tool calls visible in a side panel
            • On close, archive/delete the ephemeral conversation
          */}
          <div className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground">
            {t('admin.skills.playground.comingSoon')}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SkillPlaygroundDrawer;
